import {
  FC,
  Fragment,
  ReactNode,
  memo,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation, type TFunction } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { markdownToHtmlSync } from '@kizen/parse-md';
import { MarkdownContainer } from 'ts-components/Chat/styles';
import { useSanitizedHtml } from 'hooks/useSanitizedHtml';
import { highlightCode } from 'app/highlightCode';
import type { ChatItem } from 'ts-components/PiChat/reducer';
import {
  shortenWorkspacePaths,
  toolArgsLabel,
  toolPreview,
} from 'ts-components/PiChat/toolLabels';
import {
  groupChatEvents,
  type ChatEvent,
} from 'ts-components/PiChat/chatEvents';
import { isScrollUpIntent, nextStickToBottom } from './chatScroll';
import {
  groupToolRuns,
  groupsEveryTool,
  toolGroupLabel,
  toolGroupStatus,
  toolGroupTarget,
  type ChatSegment,
} from './toolGroups';
import {
  formatSize,
  selectActivity,
  showsActivityRow,
  type Activity,
} from './activity';
import { ScrollFades } from './ScrollFades';
import { PiChatSkeleton, type ChatPhase } from './PiChatSkeleton';
import { noticeCopy } from './errorCopy';
import {
  AssistantText,
  ChatFrame,
  ChatList,
  ChatScroll,
  EmptyChat,
  NoticeCode,
  NoticeDetails,
  NoticeRow,
  RowChevron,
  StatusIcon,
  ThinkingBody,
  ThinkingDetails,
  ToolArgs,
  ToolCard,
  ToolDetail,
  ToolGroupBody,
  ToolGroupLabel,
  ToolMeta,
  VisuallyHidden,
  ActivityLabel,
  ToolName,
  ToolPreview,
  ToolRowStatic,
  UserBubble,
  WorkingRow,
} from './styles';

export type ToolItem = Extract<ChatItem, { kind: 'tool' }>;
type AssistantItem = Extract<ChatItem, { kind: 'assistant' }>;

const SANITIZE_OPTIONS = { ADD_ATTR: ['target'] };

const Markdown = memo(({ text }: { text: string }) => {
  const html = useMemo(
    () => markdownToHtmlSync(text, { forceExternalLinks: true }),
    [text]
  );
  const sanitized = useSanitizedHtml(html, SANITIZE_OPTIONS);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.querySelectorAll('pre > code').forEach((code) => {
      code.innerHTML = highlightCode(
        code.textContent ?? '',
        /language-(\S+)/.exec(code.className)?.[1] ?? null
      );
    });
  }, [sanitized]);
  return (
    <MarkdownContainer
      ref={ref}
      className="kds"
      color="font-primary"
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
});
Markdown.displayName = 'Markdown';

const ToolStatusIcon: FC<{ status: ToolItem['status']; label: string }> = ({
  status,
  label,
}) => (
  <StatusIcon role="img" aria-label={label}>
    {status === 'running' || status === 'generating' ? (
      <Icon icon="action-loading" size="sm" color="font-placeholder" />
    ) : status === 'ok' ? (
      <Icon icon="custom-circle-check" size="sm" color="feedback-success" />
    ) : status === 'cancelled' ? (
      <Icon icon="action-close" size="sm" color="font-placeholder" />
    ) : (
      <Icon icon="message-warning-triangle" size="sm" color="feedback-error" />
    )}
  </StatusIcon>
);

const toolStatusLabel = (status: ToolItem['status'], t: TFunction) =>
  status === 'generating'
    ? t('Writing')
    : status === 'running'
      ? t('Running')
      : status === 'ok'
        ? t('Succeeded')
        : status === 'cancelled'
          ? t('Stopped')
          : t('Failed');

export const PiChatToolRow: FC<{ item: ToolItem }> = ({ item }) => {
  const { t } = useTranslation();
  const args = toolArgsLabel(item.argsSummary);
  const preview = toolPreview(item);
  const detail = shortenWorkspacePaths(
    (item.status === 'running' ? item.partial : item.summary).trim()
  );
  const head = (
    <>
      <ToolStatusIcon
        status={item.status}
        label={toolStatusLabel(item.status, t)}
      />
      <ToolName>{item.label ?? item.toolName}</ToolName>
      {args ? (
        <ToolArgs title={args}>{args}</ToolArgs>
      ) : (
        <ToolPreview title={preview}>{preview}</ToolPreview>
      )}
      {item.status === 'generating' ? (
        <ToolMeta data-qa-id="pi-chat-tool-generating">
          {t('writing {{size}}', { size: formatSize(item.chars ?? 0) })}
        </ToolMeta>
      ) : null}
    </>
  );

  if (!detail) {
    return <ToolRowStatic data-qa-id="pi-chat-tool-row">{head}</ToolRowStatic>;
  }
  return (
    <ToolCard>
      <summary data-qa-id="pi-chat-tool-row-toggle">
        {head}
        <RowChevron className="ai-chevron" aria-hidden />
      </summary>
      <ToolDetail>{detail}</ToolDetail>
    </ToolCard>
  );
};

const AssistantRow: FC<{ item: AssistantItem }> = ({ item }) => {
  const { t } = useTranslation();
  return (
    <>
      {item.thinking ? (
        <ThinkingDetails>
          <summary data-qa-id="pi-chat-thinking-toggle">
            <RowChevron className="ai-chevron" aria-hidden />
            {!item.done && !item.text ? t('Thinking…') : t('Thinking')}
          </summary>
          <ThinkingBody>{item.thinking}</ThinkingBody>
        </ThinkingDetails>
      ) : null}
      {item.text ? (
        <AssistantText>
          <Markdown text={item.text} />
        </AssistantText>
      ) : null}
    </>
  );
};

type ToolGroupSegment = Extract<ChatSegment, { kind: 'toolGroup' }>;

const PiChatToolGroup: FC<{
  group: ToolGroupSegment;
  renderToolRow: (item: ToolItem) => ReactNode;
}> = ({ group, renderToolRow }) => {
  const { t } = useTranslation();
  const status = toolGroupStatus(group.tools);
  const target = toolGroupTarget(group.tools);
  const latest = group.tools[group.tools.length - 1];
  return (
    <ToolCard data-qa-id="pi-chat-tool-group">
      <summary data-qa-id="pi-chat-tool-group-toggle">
        <ToolStatusIcon status={status} label={toolStatusLabel(status, t)} />
        <ToolGroupLabel>{toolGroupLabel(group.tools, t)}</ToolGroupLabel>
        <ToolArgs title={target}>{target}</ToolArgs>
        {latest.status === 'generating' ? (
          <ToolMeta data-qa-id="pi-chat-tool-group-generating">
            {t('writing {{size}}', { size: formatSize(latest.chars ?? 0) })}
          </ToolMeta>
        ) : null}
        <RowChevron className="ai-chevron" aria-hidden />
      </summary>
      <ToolGroupBody>
        {group.members.map((member) => (
          <Fragment key={member.id}>
            {member.kind === 'tool' ? (
              renderToolRow(member)
            ) : (
              <AssistantRow item={member} />
            )}
          </Fragment>
        ))}
      </ToolGroupBody>
    </ToolCard>
  );
};

const activityLabel = (activity: Activity, t: TFunction): string => {
  switch (activity.kind) {
    case 'thinking':
      return t('Thinking…');
    case 'writing':
      return t('Writing {{target}}… {{size}}', {
        target: activity.target,
        size: activity.size,
      });
    case 'running':
      return t('Running {{tool}}…', { tool: activity.tool });
    case 'responding':
      return t('Responding…');
    case 'working':
      return t('Working…');
  }
};

const activityPhaseLabel = (activity: Activity, t: TFunction): string =>
  activity.kind === 'writing'
    ? t('Writing {{target}}…', { target: activity.target })
    : activityLabel(activity, t);

const NoticeItemRow: FC<{
  item: Extract<ChatItem, { kind: 'notice' }>;
  profile?: string;
}> = ({ item, profile }) => {
  const { t } = useTranslation();
  const { text, details, code } = noticeCopy(item, t, profile);
  return (
    <NoticeRow $error={item.isError}>
      {text}
      {details?.length ? (
        <NoticeDetails>
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </NoticeDetails>
      ) : null}
      {code ? <NoticeCode>{code}</NoticeCode> : null}
    </NoticeRow>
  );
};

const ChatItemRow: FC<{
  item: ChatItem;
  renderToolRow: (item: ToolItem) => ReactNode;
  profile?: string;
}> = ({ item, renderToolRow, profile }) => {
  switch (item.kind) {
    case 'user':
      return <UserBubble>{item.text}</UserBubble>;
    case 'assistant':
      return <AssistantRow item={item} />;
    case 'tool':
      return <>{renderToolRow(item)}</>;
    case 'notice':
      return <NoticeItemRow item={item} profile={profile} />;
  }
};

const renderDefaultToolRow = (item: ToolItem) => <PiChatToolRow item={item} />;

interface PiChatLogProps<E extends ChatEvent> {
  phase: ChatPhase;
  loadingRevealed: boolean;
  items: ChatItem[];
  isStreaming: boolean;
  emptyPrompt: string;
  events: E[];
  renderEvent: (event: E) => ReactNode;
  renderToolRow?: (item: ToolItem) => ReactNode;
  groupsTool?: (item: ToolItem) => boolean;
  /** The builder profile, so notices can name what is being edited. */
  profile?: string;
}

export const PiChatLog = <E extends ChatEvent>({
  phase,
  loadingRevealed,
  items,
  isStreaming,
  emptyPrompt,
  events,
  renderEvent,
  renderToolRow = renderDefaultToolRow,
  groupsTool = groupsEveryTool,
  profile,
}: PiChatLogProps<E>) => {
  const { t } = useTranslation();
  const activity = selectActivity(items, isStreaming);
  const isEmpty = items.length === 0 && !isStreaming;
  const showSkeleton = phase === 'loading' && isEmpty;
  const showEmptyPrompt = phase === 'ready' && isEmpty;
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const releaseOnIntent = (intent: Parameters<typeof isScrollUpIntent>[0]) => {
    if (isScrollUpIntent(intent)) stickToBottomRef.current = false;
  };
  const handleScroll = () => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    stickToBottomRef.current = nextStickToBottom(
      stickToBottomRef.current,
      lastScrollTopRef.current,
      scroller
    );
    lastScrollTopRef.current = scroller.scrollTop;
  };

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (scroller && stickToBottomRef.current) {
      scroller.scrollTop = scroller.scrollHeight;
    }
  }, [items, events, isStreaming]);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const list = listRef.current;
    if (!scroller || !list) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(scroller);
    observer.observe(list);
    return () => observer.disconnect();
  }, []);
  const eventsByIndex = useMemo(
    () => groupChatEvents(events, items.length),
    [events, items.length]
  );
  const segments = useMemo(
    () => groupToolRuns(items, eventsByIndex, groupsTool),
    [items, eventsByIndex, groupsTool]
  );

  return (
    <ChatFrame>
      <ScrollFades
        scrollRef={scrollRef}
        deps={[items.length, events.length, isStreaming]}
      />
      <ChatScroll
        ref={scrollRef}
        className="kds-expanding-scrollbar"
        onScroll={handleScroll}
        onWheel={(event) =>
          releaseOnIntent({ type: 'wheel', deltaY: event.deltaY })
        }
        onTouchMove={() => releaseOnIntent({ type: 'touchmove' })}
        onKeyDown={(event) =>
          releaseOnIntent({ type: 'keydown', key: event.key })
        }
        data-qa-id="pi-chat-chat"
      >
        <ChatList
          ref={listRef}
          role="log"
          aria-live="polite"
          $empty={showEmptyPrompt}
        >
          {showSkeleton ? <PiChatSkeleton revealed={loadingRevealed} /> : null}
          {showEmptyPrompt ? (
            <EmptyChat>
              <Typography color="font-placeholder" size="sm">
                {emptyPrompt}
              </Typography>
            </EmptyChat>
          ) : null}
          {eventsByIndex
            .get(0)
            ?.map((event) => (
              <Fragment key={event.id}>{renderEvent(event)}</Fragment>
            ))}
          {segments.map((segment) => (
            <Fragment key={segment.key}>
              {segment.kind === 'toolGroup' ? (
                <PiChatToolGroup
                  group={segment}
                  renderToolRow={renderToolRow}
                />
              ) : (
                <ChatItemRow
                  item={segment.item}
                  renderToolRow={renderToolRow}
                  profile={profile}
                />
              )}
              {eventsByIndex
                .get(segment.afterCount)
                ?.map((event) => (
                  <Fragment key={event.id}>{renderEvent(event)}</Fragment>
                ))}
            </Fragment>
          ))}
          {activity && showsActivityRow(activity) ? (
            <WorkingRow data-qa-id="pi-chat-working" aria-hidden>
              <Icon icon="action-loading" size="sm" color="font-placeholder" />
              <ActivityLabel>{activityLabel(activity, t)}</ActivityLabel>
            </WorkingRow>
          ) : null}
        </ChatList>
      </ChatScroll>
      <VisuallyHidden
        role="status"
        aria-live="polite"
        data-qa-id="pi-chat-activity-announcement"
      >
        {activity
          ? activityPhaseLabel(activity, t)
          : showSkeleton && loadingRevealed
            ? t('Loading conversation…')
            : ''}
      </VisuallyHidden>
    </ChatFrame>
  );
};
